import { useEffect, useRef, useState } from 'react';
import {
    BasicResponsiveImage,
    Box,
    Button,
    FlexBox,
    FormField,
    FormInputGroup,
    FormLabel,
    FormLabelOptional,
    ModalDialog,
    ModalDialogBody,
    ModalDialogButtons,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogFooter,
    ModalDialogHeader,
    ModalDialogNav,
    StandardForm,
    TextArea,
    Typography,
    responsive,
} from '@vp/swan';
import { FormattedReactMessage, __ } from '@99designs/i18n';
import { StarRating, StarRatingProps } from '../StarRating';

const ResponsiveModalDialogContent = responsive(ModalDialogContent);

export type Criterion = {
    type: string;
    rating: number;
};

export type ReviewRatings = {
    overall: number;
    comment: string;
    criteria: Criterion[];
};

export type CriterionConfig = {
    type: string;
    label: string;
    subtitle: string;
};

export type DesignerReviewModalProps = {
    isOpen: boolean;
    onRequestDismiss: () => void;
    onSubmit: (ratings: ReviewRatings) => void;
    onSkip: () => void;
    title?: string;
    submitLabel?: string;
    skipLabel?: string;
    designerName?: string;
    designerAvatarUrl?: string;
    initialReview?: ReviewRatings;
    RatingComponent?: React.ComponentType<StarRatingProps>;
    defaultRevealed?: boolean;
    criteria?: CriterionConfig[];
    ratingLabels?: Record<number, string>;
    isSubmitting?: boolean;
};

const criteriaToRatings = (criteria: Criterion[]) =>
    criteria.reduce<Record<string, number>>((ratings, criterion) => {
        ratings[criterion.type] = criterion.rating;
        return ratings;
    }, {});

export const DesignerReviewModal = ({
    isOpen,
    onRequestDismiss,
    onSubmit,
    onSkip,
    title,
    submitLabel,
    skipLabel,
    designerName,
    designerAvatarUrl,
    initialReview,
    RatingComponent = StarRating,
    defaultRevealed,
    criteria = [
        {
            type: 'CRITERION_DESIGN_ACCURACY',
            label: __('Design accuracy'),
            subtitle: __('Did they deliver what you asked for?'),
        },
        {
            type: 'CRITERION_DESIGN_OUTCOME',
            label: __('Design outcome'),
            subtitle: __('How was the design?'),
        },
        {
            type: 'CRITERION_COMMUNICATION',
            label: __('Communication'),
            subtitle: __('Were they easy to work with?'),
        },
    ],
    ratingLabels = {
        1: __('Poor'),
        2: __('Fair'),
        3: __('Good'),
        4: __('Very good'),
        5: __('Excellent'),
    },
    isSubmitting = false,
}: DesignerReviewModalProps) => {
    const [overall, setOverall] = useState<number | undefined>(initialReview?.overall);
    const initialCriteriaRatings = criteriaToRatings(initialReview?.criteria ?? []);
    const [criteriaRatings, setCriteriaRatings] =
        useState<Record<string, number>>(initialCriteriaRatings);
    const [comment, setComment] = useState(initialReview?.comment ?? '');

    // Move focus to the title when the dialog opens. SWAN reuses a single <dialog> element across open/close, so
    // without this VoiceOver can be left on the page body ("web content") or read a stale summary on reopen. Focusing
    // the title reliably pulls the screen reader into the dialog and announces its name.

    const titleRef = useRef<HTMLElement>(null);
    useEffect(() => {
        if (!isOpen) return undefined;
        const frame = requestAnimationFrame(() => titleRef.current?.focus());
        return () => cancelAnimationFrame(frame);
    }, [isOpen]);

    const isDetailedReviewVisible = defaultRevealed ?? overall !== undefined;
    const hasReviewChanged =
        !initialReview ||
        overall !== initialReview.overall ||
        comment !== initialReview.comment ||
        criteria.some(({ type }) => criteriaRatings[type] !== initialCriteriaRatings[type]);

    const handleSubmit = () => {
        onSubmit({
            overall: overall ?? 0,
            comment,
            criteria: criteria.map(({ type }) => ({
                type,
                rating: criteriaRatings[type] ?? 0,
            })),
        });
    };

    const handleOverallChange = (value: number) => setOverall(value);

    const handleCriterionChange = (type: string, value: number) =>
        setCriteriaRatings((prev) => ({ ...prev, [type]: value }));

    return (
        <ModalDialog
            isOpen={isOpen}
            onRequestDismiss={onRequestDismiss}
            variant="panel-right"
            onlyRenderWhenOpen
            aria-labelledby="review-dialog-title"
        >
            <ResponsiveModalDialogContent
                padding={5}
                md={{
                    style: {
                        minWidth: '420px',
                    },
                }}
            >
                <ModalDialogNav mb={5}>
                    <ModalDialogCloseButton accessibleText={__('Close')} />
                </ModalDialogNav>
                <FlexBox style={{ height: '100%' }} flexDirection="column">
                    <Box backgroundColor="standard">
                        <ModalDialogBody>
                            <FlexBox flexDirection="column">
                                <ModalDialogHeader>
                                    <Typography
                                        ref={titleRef as React.Ref<HTMLElement>}
                                        tabIndex={-1}
                                        id="review-dialog-title"
                                        fontSkin="title-section"
                                        textAlign="center"
                                        style={{ outline: 'none' }}
                                    >
                                        {title ?? __('Review your designer')}
                                    </Typography>
                                </ModalDialogHeader>

                                <DesignerHeader
                                    designerName={designerName}
                                    designerAvatarUrl={designerAvatarUrl}
                                />
                                <FlexBox flexDirection="column" mt="to-actions">
                                    <FlexBox justifyContent="center" mb={6}>
                                        <RatingComponent
                                            value={overall}
                                            onChange={handleOverallChange}
                                            name={__('Overall rating', {
                                                _context:
                                                    'Accessible label for the overall star rating group',
                                            })}
                                            labels={ratingLabels}
                                            showTooltip
                                        />
                                    </FlexBox>

                                    <AnimatedReveal revealed={isDetailedReviewVisible}>
                                        <DetailedReview
                                            comment={comment}
                                            onCommentChange={setComment}
                                            criteria={criteria}
                                            criteriaRatings={criteriaRatings}
                                            onCriterionChange={handleCriterionChange}
                                            RatingComponent={RatingComponent}
                                        />
                                    </AnimatedReveal>
                                </FlexBox>
                            </FlexBox>
                        </ModalDialogBody>
                    </Box>
                    <ModalDialogFooter pinned>
                        <FlexBox flexDirection="column" gap="between-actions">
                            <ModalDialogButtons>
                                <Button
                                    skin="primary"
                                    width="full-width"
                                    onClick={handleSubmit}
                                    disabled={overall === undefined || !hasReviewChanged}
                                    loading={isSubmitting}
                                >
                                    {submitLabel ??
                                        (initialReview ? __('Save changes') : __('Submit review'))}
                                </Button>
                                <Button skin="secondary" width="full-width" onClick={onSkip}>
                                    {skipLabel ?? __('Cancel')}
                                </Button>
                            </ModalDialogButtons>
                        </FlexBox>
                    </ModalDialogFooter>
                </FlexBox>
            </ResponsiveModalDialogContent>
        </ModalDialog>
    );
};

type AnimatedRevealProps = {
    revealed: boolean;
    children: React.ReactNode;
};

/**
 * Animated reveal: children appear after the overall star rating is selected.
 * grid-template-rows: 0fr → 1fr collapses/expands height.
 * margin-top animates in sync to avoid a phantom gap between stars and the
 * collapsed content.
 *
 * Children are only mounted while revealed. CSS-only hiding (0fr height /
 * opacity: 0) and even `inert` still leave the sub-ratings in the accessibility
 * tree in some browser + screen reader combinations, so VoiceOver would read
 * the hidden "Tell us more…" field. Not rendering them is the only reliable
 * way to keep them out of the screen reader and tab order until they're
 * visible.
 *
 * The always-present wrapper carries `role="status"` (which implies
 * `aria-live="polite"`) so that when the content mounts (after the overall
 * rating is chosen) the screen reader announces the newly revealed section
 * without moving focus away from the stars. The explicit role also gives the
 * region an accessible identity that tests can target via `getByRole`
 * instead of DOM traversal.
 */
const AnimatedReveal = ({ revealed, children }: AnimatedRevealProps) => (
    <Box
        style={{
            display: 'grid',
            gridTemplateRows: revealed ? '1fr' : '0fr',
            opacity: revealed ? 1 : 0,
            marginTop: revealed ? 'var(--swan-sem-space-6)' : '0',
            transition: 'grid-template-rows 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease',
        }}
    >
        <div style={{ overflow: 'hidden' }} role="status" aria-live="polite">
            {revealed ? children : null}
        </div>
    </Box>
);

type DesignerHeaderProps = {
    designerName?: string;
    designerAvatarUrl?: string;
};

const DesignerHeader = ({ designerName, designerAvatarUrl }: DesignerHeaderProps) => (
    <FlexBox justifyContent="center">
        <FlexBox flexDirection="column" alignItems="center" gap="5">
            <Typography fontSkin="body-standard" textAlign="center">
                {designerName ? (
                    <FormattedReactMessage
                        message={__('How would you rate your designer <Name/>?')}
                        replacements={{
                            Name: (
                                <Typography fontSkin="body-standard-bold" as="span">
                                    {designerName}
                                </Typography>
                            ),
                        }}
                    />
                ) : (
                    __('How would you rate your designer?')
                )}
            </Typography>
            {designerAvatarUrl && designerName && (
                <Box
                    style={{
                        width: 'var(--swan-sem-space-11)',
                        minWidth: 'var(--swan-sem-space-11)',
                    }}
                >
                    <BasicResponsiveImage
                        src={designerAvatarUrl}
                        alt={designerName}
                        aspectRatio={1}
                        style={{ borderRadius: '50%' }}
                    />
                </Box>
            )}
        </FlexBox>
    </FlexBox>
);

type DetailedReviewProps = {
    comment: string;
    onCommentChange: (value: string) => void;
    criteria: CriterionConfig[];
    criteriaRatings: Record<string, number>;
    onCriterionChange: (type: string, value: number) => void;
    RatingComponent: React.ComponentType<StarRatingProps>;
};

const DetailedReview = ({
    comment,
    onCommentChange,
    criteria,
    criteriaRatings,
    onCriterionChange,
    RatingComponent,
}: DetailedReviewProps) => (
    <FlexBox flexDirection="column" gap="6">
        <StandardForm>
            <FormField>
                <FormLabel>
                    {__('Tell us more about your experience')}{' '}
                    <FormLabelOptional>{__('(Optional)')}</FormLabelOptional>
                </FormLabel>
                <FormInputGroup>
                    <TextArea
                        value={comment}
                        onChange={(e) => onCommentChange(e.target.value)}
                        placeholder={__('Write your public review')}
                        fullWidth
                        resize="vertical"
                        rows={4}
                    />
                </FormInputGroup>
            </FormField>
        </StandardForm>

        <FlexBox flexDirection="column" gap="7">
            {criteria.map((config) => (
                <FlexBox
                    key={config.type}
                    alignItems="center"
                    justifyContent="space-between"
                    gap={6}
                >
                    <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography fontSkin="body-standard-bold">{config.label}</Typography>
                        <Typography fontSkin="body-small" textColor="subtle">
                            {config.subtitle}
                        </Typography>
                    </Box>
                    <RatingComponent
                        size="small"
                        name={config.label}
                        value={criteriaRatings[config.type]}
                        onChange={(val) => onCriterionChange(config.type, val)}
                    />
                </FlexBox>
            ))}
        </FlexBox>
    </FlexBox>
);
